/**
 * Marker computation for the timeline UI.
 *
 * Collects error, compaction, and branch markers from a TimelineSpan's
 * content at configurable depth levels (direct, children, recursive).
 */

import type { CompactionEvent, Event } from "@tsmono/inspect-common/types";
import { formatDuration } from "@tsmono/util";

import type { TimelineEvent, TimelineSpan } from "./core";
import { computeTimeEnvelope, formatTokenCount } from "./swimlaneLayout";

// =============================================================================
// Types
// =============================================================================

export type MarkerKind = "error" | "compaction" | "branch";

export interface TimelineMarker {
  kind: MarkerKind;
  timestamp: Date;
  reference: string;
  /** Human-readable detail for tooltip display. */
  tooltip: string;
}

export type MarkerDepth = "direct" | "children" | "recursive";

export interface MarkerConfig {
  kinds: MarkerKind[];
  depth: MarkerDepth;
}

export const defaultMarkerConfig: MarkerConfig = {
  kinds: ["compaction", "branch"],
  depth: "direct",
};

// =============================================================================
// Event Classification
// =============================================================================

/**
 * Returns true if the event is an error event.
 *
 * An event is an error if:
 * - It's a ToolEvent with a non-null `.error`
 * - It's a ModelEvent with a non-null `.error`
 *
 * Noneable fields are stripped during serialization (`exclude_none=True`), so
 * `null`/missing both manifest as `undefined` at runtime — hence the `!= null`
 * check. See packages/inspect-common/src/types/index.ts.
 *
 * Note: `ModelOutput.error` (a soft-refusal/content-filter channel populated
 * by some providers without raising) is intentionally excluded — the
 * transcript's ModelEventView only renders `event.error`, so flagging on
 * `output.error` produces markers that navigate to a card with no visible
 * error.
 */
export function isErrorEvent(event: Event): boolean {
  if (event.event === "tool") {
    return event.error != null;
  }
  if (event.event === "model") {
    return event.error != null;
  }
  return false;
}

/**
 * Returns true if the event is a compaction event.
 */
export function isCompactionEvent(event: Event): event is CompactionEvent {
  return event.event === "compaction";
}

/**
 * Builds a tooltip string for an error event.
 */
function errorTooltip(event: Event): string {
  if (event.event === "tool") {
    const msg = event.error?.message ?? "Unknown error";
    return `Error (${event.function}): ${msg}`;
  }
  if (event.event === "model") {
    const msg =
      (typeof event.error === "string" ? event.error : null) ?? "Unknown error";
    return `Error (${event.model}): ${msg}`;
  }
  return "Error";
}

// =============================================================================
// Main Function
// =============================================================================

/**
 * Collects timeline markers from a TimelineSpan at the specified depth.
 *
 * - `"direct"`: Only markers from the span's own TimelineEvent content.
 * - `"children"`: Own events + events from direct child spans.
 * - `"recursive"`: Full subtree traversal.
 *
 * Branch markers are always collected from the span's own branches
 * (not from child spans), regardless of depth.
 *
 * Results are sorted by timestamp.
 */
export function collectMarkers(
  node: TimelineSpan,
  depth: MarkerDepth
): TimelineMarker[] {
  const markers: TimelineMarker[] = [];

  // Collect event markers at the specified depth
  collectEventMarkers(node, depth, 0, markers);

  // Collect branch markers from this span's branches only
  collectBranchMarkers(node, markers);

  // Sort by timestamp
  markers.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  return markers;
}

// =============================================================================
// Internal Helpers
// =============================================================================

/**
 * Collects error and compaction markers from event nodes.
 *
 * @param node The span to scan
 * @param depth The depth mode
 * @param currentLevel 0 = the root node itself, 1 = direct children, etc.
 * @param markers Accumulator array
 */
function collectEventMarkers(
  node: TimelineSpan,
  depth: MarkerDepth,
  currentLevel: number,
  markers: TimelineMarker[]
): void {
  for (const item of node.content) {
    if (item.type === "event") {
      addEventMarker(item, markers);
    } else if (shouldDescend(depth, currentLevel)) {
      collectEventMarkers(item, depth, currentLevel + 1, markers);
    }
  }
}

/**
 * Determines whether to descend into a child span based on depth mode.
 */
function shouldDescend(depth: MarkerDepth, currentLevel: number): boolean {
  if (depth === "direct") return false;
  if (depth === "children") return currentLevel === 0;
  // "recursive"
  return true;
}

/**
 * Adds a marker for a timeline event if it's an error or compaction event.
 */
function addEventMarker(
  eventNode: TimelineEvent,
  markers: TimelineMarker[]
): void {
  const event = eventNode.event;
  const uuid = event.uuid;

  if (isErrorEvent(event)) {
    markers.push({
      kind: "error",
      timestamp: eventNode.startTime(),
      reference: uuid ?? "",
      tooltip: errorTooltip(event),
    });
  } else if (isCompactionEvent(event)) {
    const before = event.tokens_before?.toLocaleString() ?? "?";
    const after = event.tokens_after?.toLocaleString() ?? "?";
    markers.push({
      kind: "compaction",
      timestamp: eventNode.startTime(),
      reference: uuid ?? "",
      tooltip: `Context compaction: ${before} → ${after} tokens`,
    });
  }
}

/**
 * Collects branch markers from a span's branches.
 *
 * Emits one marker per branch, positioned at the fork point event in the
 * parent span (resolved via the branch's `branchedFrom` identifier).
 * Clicking a branch marker toggles the showBranches display option.
 */
function collectBranchMarkers(
  node: TimelineSpan,
  markers: TimelineMarker[]
): void {
  for (const branch of node.branches) {
    markers.push({
      kind: "branch",
      timestamp: resolveForkTimestamp(node, branch),
      reference: branch.branchedFrom ?? "",
      tooltip: branchTooltip([branch]),
    });
  }
}

/**
 * Resolves the fork point timestamp from the parent span's content.
 *
 * `branch.branchedFrom` is a message ID (not an event UUID). Searches
 * parent events for one that produced or carries this message ID.
 *
 * When `branchedFrom` is empty the branch is unrolled from the very
 * beginning, so we return the parent's start time.
 *
 * Falls back to `parent.startTime()` when no matching event is found.
 */
export function resolveForkTimestamp(
  parent: TimelineSpan,
  branch: TimelineSpan
): Date {
  if (!branch.branchedFrom) return parent.startTime();
  for (const item of parent.content) {
    if (item.type === "event" && item.matchesForkPoint(branch.branchedFrom)) {
      return item.startTime();
    }
  }
  return parent.startTime();
}

/**
 * Builds a tooltip string summarizing branches at a fork point.
 */
function branchTooltip(branches: TimelineSpan[]): string {
  const count = branches.length;
  const totalTokens = branches.reduce((sum, b) => sum + b.totalTokens(), 0);
  const tokenStr = formatCompactTokens(totalTokens);

  // Compute combined duration: earliest start to latest end
  const envelope = computeTimeEnvelope(branches);
  const duration = formatDuration(envelope.startTime, envelope.endTime);

  const label = count === 1 ? "1 branch" : `${count} branches`;
  return `${label} (${tokenStr}, ${duration})`;
}

/**
 * Formats a token count compactly with " tokens" suffix: "48.5k tokens", "1.2M tokens", etc.
 */
function formatCompactTokens(tokens: number): string {
  return `${formatTokenCount(tokens)} tokens`;
}
